// The end of the page: the questions and the footer. (The closing CTA and the
// contact form live in final-lantern.tsx.)
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CRWIcon } from '../components/icons';
import { Logo, SectionHead } from '../components/primitives';
import { BRAND } from '../lib/site';
import { NAV_GET, NAV_PROOF } from './nav';

/* ---------- FAQ ---------- */
export function FAQ() {
  const qs: [string, string][] = [
    ['Is this AI slop?', 'No. The film is built from real footage and interview clips. AI-made scenes are one layer of seven, used where no footage exists, and you approve every still before it is animated.'],
    ['Do I keep a Premiere project?', 'Yes. You get the Adobe Premiere Pro project and a master file. You or your editor can open it and change anything.'],
    ['What do you need from me?', 'The recorded voiceover, the script, and what you would hand a new editor on day one: your template, fonts, lower thirds and music rules. Links to your past videos help us match the style.'],
    ['How long does it take?', 'About two weeks for the first film, while we learn your style. Five working days for each film after that.'],
    ['What if I do not like it?', 'You pay for the first film only if you publish it. Before that you approve the look and the stills, then watch the cut and send notes.'],
    ['Who owns the film?', 'Your channel. The film and the Premiere project are yours.'],
    ['Which channels does it fit?', 'Narrated long-form channels made from found footage and AI scenes: documentaries, history, business, geopolitics, video essays. It does not fit channels built around a host on camera, or channels that are mostly animation.'],
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" style={{ padding: '70px 24px 90px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <SectionHead center label="Questions" title="What channels ask first." />
        <div>
          {qs.map(([q, a], i) => {
            const on = open === i;
            return (
              <div key={i} style={{ borderBottom: '1px solid var(--line-1)' }}>
                <button onClick={() => setOpen(on ? -1 : i)} className="crw-faq-q" aria-expanded={on}>
                  <b style={{ flex: 1, fontSize: 15.5, fontWeight: 600, color: on ? 'var(--fg-1)' : 'var(--fg-2)', transition: 'color 150ms var(--ease-out)' }}>{q}</b>
                  <span style={{ color: on ? 'var(--fg-1)' : 'var(--fg-3)', display: 'inline-flex', transition: 'transform 250ms var(--ease-out), color 150ms var(--ease-out)', transform: on ? 'rotate(45deg)' : 'none' }}><CRWIcon d="plus" size={16} /></span>
                </button>
                <div style={{ display: 'grid', gridTemplateRows: on ? '1fr' : '0fr', transition: 'grid-template-rows 300ms var(--ease-out)' }}>
                  <div style={{ overflow: 'hidden', minHeight: 0 }}>
                    <p style={{ margin: '0 0 20px', padding: '0 4px', fontSize: 14, lineHeight: 1.7, color: 'var(--fg-3)', maxWidth: '60ch' }}>{a}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- FOOTER ---------- */
type FootLink = { label: string; hash: string };
const COLUMNS: { head: string; links: FootLink[] }[] = [
  { head: 'What you get', links: NAV_GET.map(({ label, hash }) => ({ label, hash })) },
  { head: 'Proof', links: NAV_PROOF.map(({ label, hash }) => ({ label, hash })) },
  { head: 'Start', links: [{ label: 'The deal', hash: '#deal' }, { label: 'Get a quote', hash: '#contact' }] },
];

export function SiteFooter() {
  return (
    <footer style={{ borderTop: '1px solid var(--line-1)', padding: '54px 24px 30px', position: 'relative', overflow: 'hidden' }}>
      {/* faint blueprint grid, fading upward */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'linear-gradient(180deg, transparent, black 60%)', WebkitMaskImage: 'linear-gradient(180deg, transparent, black 60%)', pointerEvents: 'none' }}></div>
      {/* the ghost wordmark: the room's name, barely lit */}
      <div aria-hidden="true" className="crw-foot-ghost" style={{ position: 'absolute', left: '50%', bottom: '-0.08em', transform: 'translateX(-50%)', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '15vw', lineHeight: 1, letterSpacing: '0.005em', color: 'rgba(244,245,247,0.027)', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none' }}>{BRAND}</div>
      <div style={{ position: 'relative', maxWidth: 1120, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 36 }}>
        <div className="onft-grid">
          <div className="onft-brand">
            <Link to="/" className="crw-logo-link" style={{ textDecoration: 'none' }}><Logo size={22} /></Link>
            <p>Finished documentary films for YouTube channels.</p>
          </div>
          {COLUMNS.map((c) => (
            <div key={c.head} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              <span className="cr-label" style={{ marginBottom: 3 }}>{c.head}</span>
              {c.links.map((l) => <Link key={l.label} to={{ pathname: '/', hash: l.hash }} className="crw-footer-link onft-link">{l.label}</Link>)}
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--line-1)', paddingTop: 18, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-4)', letterSpacing: '0.05em' }}>
            © 2026 {BRAND}. All rights reserved.
          </span>
          <span style={{ flex: 1 }}></span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--fg-4)', letterSpacing: '0.07em', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            MADE IN THE EU
            {/* inline SVG flag: the emoji renders as the letters "EU" on Windows */}
            <svg width="18" height="12" viewBox="0 0 24 16" aria-hidden="true" style={{ display: 'block', borderRadius: 2, flex: 'none' }}>
              <rect width="24" height="16" rx="2" fill="#003399" />
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * Math.PI) / 6;
                return <circle key={i} cx={(12 + 5 * Math.sin(a)).toFixed(2)} cy={(8 - 5 * Math.cos(a)).toFixed(2)} r="1" fill="#FFCC00" />;
              })}
            </svg>
          </span>
        </div>
      </div>
    </footer>
  );
}
